import React from 'react';
import { AvatarProps, AvatarSize, AvatarTone } from '../../../types/DesignSystem/Avatar';
import { withClassName } from '../cn';

/**
 * Initials from a display name — first letter of the first two words, uppercased.
 *
 * Exported because it is the one piece of this component worth testing on its own,
 * and because the GMS top bar feeds it `getUserName()`, which returns a **first
 * name only**. One initial is therefore the normal case here, not a degenerate one.
 *
 * The `?` fallback matters: `AuthManager.getAuthUser()` can resolve before the
 * profile does, and an avatar that renders an empty circle for a frame reads as a
 * loading bug rather than as an avatar.
 */
export const initialsFrom = (name: string): string => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words.slice(0, 2).map((w) => [...w][0]!.toUpperCase()).join('');
};

/** px, and the Tailwind box for each. See `AvatarSize` for why this is not the control ladder. */
const BOX: Record<AvatarSize, string> = {
  sm: 'tw-w-6 tw-h-6',    // 24
  md: 'tw-w-8 tw-h-8',    // 32
  lg: 'tw-w-10 tw-h-10',  // 40
};

/**
 * Type token per size rather than a scale factor. `sub-body-supporting` is 12/500 and
 * `title` is 14/500 — both already in the system, so an avatar's initials are never a
 * font size nobody else uses.
 */
const TYPE: Record<AvatarSize, string> = {
  sm: 'tw-text-sub-body-supporting',
  md: 'tw-text-sub-body-supporting',
  lg: 'tw-text-title',
};

/**
 * Ground/ink pairs, both palette roles so dark mode comes free.
 *
 * `accent` measures **16.0:1** in light (`#030562` on `#eef4ff`) — far past AA, which
 * is what you want from two characters at 12px.
 */
/*
 * § 297 — the tone pairs are `Status`'s tint/ink pairs, so every one is already measured
 * at 12px/500 (§ 36: info 7.85, success 7.54, purple 9.06, neutral 5.18 against its own
 * tint) and themes with the rest of the system. `accent` and `neutral` are unchanged.
 */
type ResolvedTone = Exclude<AvatarTone, 'auto'>;
const TONE: Record<ResolvedTone, string> = {
  accent: 'tw-bg-accent-tint tw-text-accent',
  neutral: 'tw-bg-tone-neutral-tint tw-text-tone-neutral-ink',
  info: 'tw-bg-tone-info-tint tw-text-tone-info-ink',
  success: 'tw-bg-tone-success-tint tw-text-tone-success-ink',
  purple: 'tw-bg-tone-accent-tint tw-text-tone-accent-ink',
};

/**
 * The four tones `auto` may land on. Deliberately NOT `warning` (acid in dark, and it
 * means "something needs attention") or `danger` (it means an error), and not `accent`:
 * the selection tint under the brand ink is what a SELECTED thing wears, and a hashed
 * avatar is not selected. Exported so a list can render a legend or a test can pin it.
 */
export const AUTO_TONES: readonly ResolvedTone[] = ['info', 'success', 'purple', 'neutral'];

/**
 * Deterministic, case- and whitespace-insensitive: the same person gets the same tone on
 * every render, in every list, in every session. A small multiplicative hash rather than a
 * char-code sum, so "Ana" and "Naa" do not collide by construction.
 */
export const toneFor = (name: string): ResolvedTone => {
  const text = (name ?? '').trim().toLowerCase();
  let hash = 7;
  for (const ch of text) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return AUTO_TONES[hash % AUTO_TONES.length];
};

/**
 * The design system's avatar.
 *
 * **What it replaces.** The top bar rendered `react-avatar` — an npm dependency used
 * in exactly one file — with
 * `color={Avatar.getRandomColor("sitebase", ["yellow", "#FFD60A"])}` recomputed on
 * every render and `fgColor="#000000"` hardcoded. Three problems in one call: an
 * off-palette saturated yellow that cannot follow a theme, black ink that is not
 * `ink` in either theme, and a 40px mark in a row whose every other control is 36 —
 * so the least important element in the header was both the tallest and the loudest
 * thing in it.
 *
 * **`rounded-full`, not `rounded-ctl`.** The radius ramp flattened to one 4px value
 * for controls, surfaces and overlays (§ 86), and an avatar is none of those — it is
 * a *portrait frame*. A 4px-cornered initials chip is a `Badge`, which the system
 * already has.
 */
const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(function Avatar({
  name,
  src,
  alt = '',
  size = 'md',
  tone = 'accent',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* § 249 — a failed load falls back to initials; a NEW src gets a fresh try. */
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [src]);
  const showImage = Boolean(src) && !failed;
  /* § 297 — `auto` resolves here, once, so the class and `data-tone` cannot disagree. */
  const resolvedTone: ResolvedTone = tone === 'auto' ? toneFor(name) : tone;
  return (
    <span
      /* rest first — the rule across this design system, see Button.tsx. It is what
         lets a caller pass `aria-hidden={false}` and an `aria-label` to override the
         decorative default below. */
      {...rest}
      ref={ref}
      /* Decorative by default: in the top bar the trigger's `aria-label` names the
         control, and in the menu header the name is rendered as text right beside
         this. Announcing the initials as well is duplication, not information. */
      aria-hidden={rest['aria-hidden'] ?? true}
      className={
        'tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-shrink-0 tw-overflow-hidden '
        + 'tw-select-none tw-uppercase '
        + withClassName(`${BOX[size]} ${TYPE[size]} ${TONE[resolvedTone]}`, className)
      }
      data-tone={resolvedTone}
      data-testid={dataTestId}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          className="tw-h-full tw-w-full tw-object-cover"
          onError={() => setFailed(true)}
          data-testid={dataTestId ? `${dataTestId}-image` : undefined}
        />
      ) : (
        initialsFrom(name)
      )}
    </span>
  );
});

export default Avatar;
